import { fmtDate, PROGRESS_FIELDS, GOALS_FIELD } from "@/lib/format";

const sum = (arr, k) => arr.reduce((a, r) => a + Number(r[k] || 0), 0);

export const DEFAULT_PFIELDS = PROGRESS_FIELDS.map((f) => ({ key: f.key, label: f.mh, desc: f.en, visible: true, custom: false }));
export const pval = (p, k) => Number((k.startsWith("c_") ? p.custom?.[k] : p[k]) || 0);
const LATEST = ["active_members", "jete_prayer_cell"];

export function progressStats(list, pfields = DEFAULT_PFIELDS) {
  const sorted = [...list].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const latest = sorted[0] || {};
  const out = { reports: list.length, last_date: latest.date || "" };
  pfields.forEach(({ key: k }) => {
    const total = list.reduce((a, p) => a + pval(p, k), 0);
    out[k] = k === "ri_jar" ? (list.length ? Math.round(total / list.length) : 0) : LATEST.includes(k) ? pval(latest, k) : total;
  });
  return out;
}

export function buildSummary({ churches, tithes = [], donations = [], progress = [], pfields }) {
  const map = new Map();
  churches.forEach((c) => map.set(c.id, { id: c.id, name: c.name, state: c.state || "", pastors: c.pastors || "", dest: {}, tithe: 0, don: 0, prog: [] }));
  const get = (r) => {
    if (!map.has(r.church_id)) map.set(r.church_id, { id: r.church_id, name: `${r.church_name} (removed)`, state: "", pastors: "", dest: {}, tithe: 0, don: 0, prog: [] });
    return map.get(r.church_id);
  };
  tithes.forEach((t) => { const g = get(t); g.dest[t.destination] = (g.dest[t.destination] || 0) + Number(t.amount || 0); g.tithe += Number(t.amount || 0); });
  donations.forEach((d) => { get(d).don += Number(d.amount || 0); });
  progress.forEach((p) => get(p).prog.push(p));
  return [...map.values()].map((g) => ({ ...g, stats: progressStats(g.prog, pfields) }));
}

const groupTotals = (rows, key) => {
  const m = {};
  rows.forEach((r) => { const k = r[key] || "(none)"; m[k] = m[k] || { count: 0, total: 0 }; m[k].count += 1; m[k].total += Number(r.amount || 0); });
  return Object.entries(m).sort((a, b) => b[1].total - a[1].total).map(([k, v]) => [k, v.count, v.total]);
};

export function summarySections({ rows, destNames, show, donations, tithes = [], typeNames = [], pfields = DEFAULT_PFIELDS }) {
  const sections = [];
  const money3 = (head) => head.map((_, i) => i).filter((i) => i >= 3);
  const footOf = (head, body) => { const foot = ["", "TOTAL", ""]; for (let i = 3; i < head.length; i++) foot.push(body.reduce((a, row) => a + Number(row[i] || 0), 0)); return foot; };
  if (show.tithes) {
    const head = ["#", "Church", "State", ...destNames, "Tithe Total"];
    const body = rows.map((r, i) => [i + 1, r.name, r.state, ...destNames.map((d) => r.dest[d] || 0), r.tithe]);
    sections.push({ title: "Tithes by Church", head, body, foot: footOf(head, body), moneyCols: money3(head) });
  }
  if (show.donations) {
    const head = ["#", "Church", "State", "Conference Collection Total"];
    const body = rows.map((r, i) => [i + 1, r.name, r.state, r.don]);
    sections.push({ title: "Conference Collection by Church", head, body, foot: footOf(head, body), moneyCols: money3(head) });
  }
  if (show.tithes && typeNames.length) {
    const by = (n) => sum(tithes.filter((t) => t.giver_type === n), "amount");
    const other = sum(tithes.filter((t) => !typeNames.includes(t.giver_type)), "amount");
    const cols = [...typeNames, ...(other ? ["Other / not set"] : [])];
    const vals = [...typeNames.map(by), ...(other ? [other] : [])];
    sections.push({ title: "Tithe Totals by Type", head: ["", ...cols, "Total"], body: [["Tithes", ...vals, sum(tithes, "amount")]], moneyCols: cols.map((_, i) => i + 1).concat(cols.length + 1) });
    const typeOf = (t) => (typeNames.includes(t.giver_type) ? t.giver_type : "Other / not set");
    const churchBody = rows.map((r, i) => {
      const mine = tithes.filter((t) => t.church_id === r.id);
      return [i + 1, r.name, ...cols.map((c) => sum(mine.filter((t) => typeOf(t) === c), "amount")), sum(mine, "amount")];
    });
    sections.push({ title: "Tithes by Church and Type", head: ["#", "Church", ...cols, "Total"], body: churchBody, foot: ["", "TOTAL", ...vals, sum(tithes, "amount")], moneyCols: cols.map((_, i) => i + 2).concat(cols.length + 2) });
  }
  if (show.progress) {
    const all = pfields.filter((f) => f.visible).map((f) => [f.key, f.key === "ri_jar" ? `${f.label} (avg)` : f.label]);
    const head = ["Church", "Reports", ...all.map(([, l]) => l)];
    const keys = ["reports", ...all.map(([k]) => k)];
    const body = rows.map((r) => [r.name, ...keys.map((k) => r.stats[k])]);
    const foot = ["TOTAL", ...keys.map((_, i) => body.reduce((a, row) => a + Number(row[i + 1] || 0), 0))];
    sections.push({ title: "Progress Report by Church", head, body, foot, kind: "progress_church", records: rows.map((r) => ({ id: r.id, church_id: r.id })) });
  }
  if (show.donations && donations.length) {
    sections.push({ title: "Conference Collection by Category", head: ["Category", "Records", "Total"], body: groupTotals(donations, "category"), moneyCols: [2] });
    sections.push({ title: "Conference Collection by Collection", head: ["Collection", "Records", "Total"], body: groupTotals(donations, "collection"), moneyCols: [2] });
  }
  return sections;
}

const change = (prev, cur) => (prev ? `${cur >= prev ? "+" : ""}${(((cur - prev) / prev) * 100).toFixed(1)}%` : cur ? "New" : "—");

export function compareSection({ rows, prevRows, year, show }) {
  const prev = Object.fromEntries(prevRows.map((r) => [r.id, r]));
  const py = Number(year) - 1;
  const cols = [...(show.tithes ? ["tithe"] : []), ...(show.donations ? ["don"] : [])];
  const label = { tithe: "Tithes", don: "Conference Collection" };
  const head = ["Church", ...cols.flatMap((k) => [`${label[k]} ${py}`, `${label[k]} ${year}`, `${label[k]} Change`])];
  const triple = (p, c) => [p, c, change(p, c)];
  const body = rows.map((r) => [r.name, ...cols.flatMap((k) => triple(prev[r.id]?.[k] || 0, r[k]))]);
  const tot = (list, k) => list.reduce((a, r) => a + r[k], 0);
  const foot = ["TOTAL", ...cols.flatMap((k) => triple(tot(prevRows, k), tot(rows, k)))];
  const moneyCols = head.map((_, i) => i).filter((i) => i > 0 && i % 3 !== 0);
  return { title: `Year Comparison: ${py} vs ${year}`, head, body, foot, moneyCols };
}

const byChurchDate = (order) => (a, b) => (order[a.church_id] ?? 999) - (order[b.church_id] ?? 999) || (a.date || "").localeCompare(b.date || "");

export function detailSections({ churches, tithes, donations, progress, show, pfields = DEFAULT_PFIELDS, goals, jemdron }) {
  const order = Object.fromEntries(churches.map((c, i) => [c.id, i]));
  const sort = byChurchDate(order);
  const sections = [];
  if (show.tithes) {
    const rows = [...tithes].sort(sort);
    const jd = jemdron ? (t) => [t.jemdron || ""] : () => [];
    sections.push({ kind: "tithes", records: rows, title: "Tithe Records", head: ["Date", "Church", ...(jemdron ? ["Jemdron"] : []), "Sent to", "Type", "Pastor / Church / Guest(s) / Ritel", "Payment", "Amount"], body: rows.map((t) => [fmtDate(t.date), t.church_name, ...jd(t), t.destination, t.giver_type || "", t.pastor_name, t.payment_type, t.amount]), foot: ["", "TOTAL", ...(jemdron ? [""] : []), "", "", "", "", rows.reduce((a, r) => a + r.amount, 0)], moneyCols: [jemdron ? 7 : 6] });
    if (jemdron && rows.length) {
      const by = {};
      rows.forEach((t) => { const k = t.jemdron || "(No Jemdron)"; by[k] = by[k] || { n: 0, total: 0 }; by[k].n += 1; by[k].total += t.amount; });
      const list = Object.entries(by).sort(([a], [b]) => (a === "(No Jemdron)") - (b === "(No Jemdron)") || a.localeCompare(b));
      sections.push({ kind: "tithes_jemdron", title: "Tithes by Jemdron", head: ["Jemdron", "Entries", "Total"], body: list.map(([k, v]) => [k, v.n, v.total]), foot: ["TOTAL", rows.length, rows.reduce((a, r) => a + r.amount, 0)], moneyCols: [2] });
    }
  }
  if (show.donations) {
    const rows = [...donations].sort(sort);
    sections.push({ kind: "donations", records: rows, title: "Conference Collection Records", head: ["Date", "Church", "Section", "Category", "Collection", "Payment", "Amount"], body: rows.map((d) => [fmtDate(d.date), d.church_name, d.destination || "", d.category, d.collection, d.payment_type, d.amount]), foot: ["", "TOTAL", "", "", "", "", rows.reduce((a, r) => a + r.amount, 0)], moneyCols: [6] });
  }
  if (show.progress) {
    const rows = [...progress].sort(sort);
    const vis = pfields.filter((f) => f.visible);
    sections.push({ kind: "progress", records: rows, title: "Progress Reports", head: ["Date", "Church", ...vis.map((f) => f.label), goals?.label || GOALS_FIELD.mh], body: rows.map((p) => [fmtDate(p.date), p.church_name, ...vis.map((f) => pval(p, f.key)), p.ta_menin_kotobar || ""]) });
  }
  return sections;
}

export function otherSections(list, detail) {
  const total = list.reduce((a, o) => a + (o.amount || 0), 0);
  const group = (keyFn) => {
    const m = {};
    list.forEach((o) => { const k = keyFn(o) || ""; m[k] = m[k] || { n: 0, people: new Set(), total: 0 }; m[k].n += 1; m[k].people.add((o.person_name || "").trim().toLowerCase() || `#${o.id}`); m[k].total += o.amount || 0; });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  };
  const byChurch = group((o) => o.church_name);
  const byEvent = group((o) => o.event_name);
  const out = [];
  if (detail) out.push({ kind: "other", title: "Other Collection Records", head: ["Date", "Church", "Name / Individual", "Event / activity", "Amount"], body: list.map((o) => [fmtDate(o.date), o.church_name, o.person_name || "", o.event_name, o.amount]), foot: ["", "TOTAL", "", "", total], moneyCols: [4] });
  out.push({ kind: "other_church", title: "Other Collections by church", head: ["Church", "People who gave", "Total"], body: byChurch.map(([k, v]) => [k, v.people.size, v.total]), foot: ["TOTAL", byChurch.reduce((a, [, v]) => a + v.people.size, 0), total], moneyCols: [2] });
  out.push({ kind: "other_event", title: "Other Collections by event", head: ["Event / activity", "Entries", "Total"], body: byEvent.map(([k, v]) => [k, v.n, v.total]), foot: ["TOTAL", list.length, total], moneyCols: [2] });
  return out;
}

